import { Box, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { config, describeDataOrigin } from '@/config/env';
import { AnimatedNumber, AnimatedNumberText } from '@/components/AnimatedNumber';
import { OPIS_KLASYFIKACJI, PrzyciskDowodu, type Dowod, type KlasyfikacjaZrodla } from '@/components/SzufladaDowodu';
import { DataQualityBadge } from '@/components/states';
import type { DataQuality } from '@/lib/dataQuality';
import type { DeliveryMetrics } from '@/lib/api/drive';
import { formatCount } from '@/lib/format';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * DOSTAWY — wspólny język trzech ekranów przestrzeni „Dostawy"
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Trzy ekrany (przegląd, wg szkół, historia) mówią o TYM SAMYM bycie i muszą mówić o nim tak
 * samo. Gdyby każdy z nich definiował „na czas" u siebie, pierwsza zmiana definicji rozjechałaby
 * je po cichu — i produkt pokazywałby dwie prawdy o jednej dostawie, nie wiadomo, w którym
 * miejscu fałszywą. Dlatego podział na wyniki, barwy, opisy i klasyfikacja źródła mieszkają tutaj.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────────────┐
 * │ PUNKTUALNOŚĆ MA TRZY WYNIKI, NIE DWA.                                                     │
 * │ na czas · PRZED CZASEM · spóźnione                                                        │
 * │                                                                                           │
 * │ Przyjazd przed czasem NIE jest trafieniem. Dostawa, która przyjechała przed otwarciem     │
 * │ stołówki, potrafi znaczyć, że jedzenie stało do przerwy — czyli dokładnie ten problem,     │
 * │ przed którym punktualność ma chronić. Wrzucenie „przed czasem" do jednego worka z „na      │
 * │ czas" zawyża wynik i ukrywa realny defekt operacyjny; wrzucenie go do „spóźnionych" jest   │
 * │ po prostu nieprawdą. Trzeci wynik istnieje w odpowiedzi API jako osobny licznik i tu jest  │
 * │ pokazywany osobno.                                                                        │
 * └──────────────────────────────────────────────────────────────────────────────────────────┘
 */

// ── KLASYFIKACJA ŹRÓDŁA ────────────────────────────────────────────────────────────────────────
// Dane dostaw na tym środowisku to zbiór KONTROLOWANY: realne API, realny schemat bazy, ale
// rekordy powstały na potrzeby weryfikacji. Ta informacja jest częścią liczby, nie przypisem —
// „92% dostaw na czas" przeczytane jako wynik sieci jest wnioskiem z danych, których nie ma.

export const KLASYFIKACJA_DRIVE: KlasyfikacjaZrodla =
  config.dataOrigin === 'production' ? 'PRODUKCJA' : 'TEST_KONTROLOWANE';

export const ZDANIE_O_ZRODLE =
  config.dataOrigin === 'production'
    ? 'Rekordy pochodzą z produkcyjnej bazy aplikacji kierowców i są czytane wyłącznie do odczytu.'
    : 'Zbiór dostaw w tym środowisku jest KONTROLOWANY: kod API i schemat bazy są realne, ale rekordy ' +
      'zostały wprowadzone na potrzeby weryfikacji. To NIE jest pełna historia operacyjna dowozów ' +
      'TuSzamy — tych liczb nie wolno czytać jak sprawozdania z sieci.';

export const ZRODLO_SUMMARY = 'Drive /analytics/delivery-summary → utrwalony status dostawy';
export const ZRODLO_BY_SCHOOL = 'Drive /analytics/delivery-by-school → utrwalony status dostawy w rozbiciu na szkoły';
export const ZRODLO_HISTORY = 'Drive /analytics/delivery-history → pojedyncze rekordy dostaw';
export const ZRODLO_COVERAGE = 'Drive /analytics/tuszama-id-coverage → identyfikatory systemu głównego na rekordach';

/** Świeżość: odczyt jest bieżący, ale źródło nie stempluje odpowiedzi. Nie zmyślamy stempla. */
export const SWIEZOSC_DRIVE =
  'odczyt bieżący z API Drive przy otwarciu ekranu; odpowiedź nie niesie stempla ostatniego zasilenia';

/**
 * Okno tolerancji — dokładnie tyle, ile wiadomo, i ani słowa więcej.
 *
 * Progi kubełków spóźnień są nazwane w kontrakcie API (do 5 / do 15 / ponad 15 minut). Samego
 * progu, przy którym dostawa przestaje być „na czas", odpowiedź NIE podaje: klasyfikacja jest
 * utrwalana w chwili dostawy po stronie aplikacji kierowcy. Zgadnięcie go tutaj byłoby wpisaniem
 * do dowodu liczby, której nikt nie zmierzył.
 */
export const OKNO_TOLERANCJI =
  'Okno tolerancji: punktem odniesienia jest planowana godzina ZAPISANA na rekordzie w chwili dostawy ' +
  '(break_time_snapshot), a nie bieżąca godzina przerwy szkoły — późniejsza zmiana godziny w panelu nie ' +
  'przepisuje historii. Kubełki spóźnień w odpowiedzi API: do 5 min, do 15 min, ponad 15 min po planowanej ' +
  'godzinie. Progu, przy którym dostawa przestaje być „na czas", odpowiedź nie podaje — klasyfikacja jest ' +
  'utrwalana po stronie systemu kierowców, a Control Center wyłącznie ją odczytuje i nie zgaduje granicy.';

/** Czego ta metryka NIE liczy. Budowane z liczników odpowiedzi, nie z pamięci autora ekranu. */
export function coSieNieLiczy(m: DeliveryMetrics): string[] {
  const lista = [
    'Dostawy PRZED CZASEM nie są trafieniem i nie wchodzą do udziału „na czas" — to osobny, trzeci wynik.',
    `Rekordy bez zapisanej planowanej godziny nie są liczone ani jako punktualne, ani jako spóźnione — ` +
      `są nieznane (${formatCount(m.without_snapshot)} w tym oknie).`,
    `Do średniej i mediany odchylenia wchodzą wyłącznie rekordy z zapisanym odchyleniem; brak nie jest ` +
      `liczony jako zero (${formatCount(m.without_delta)} bez odchylenia).`,
    'Zbiór zawiera wyłącznie rekordy ZAPISANE przez aplikację kierowcy. Dostawa, której nikt nie potwierdził, ' +
      'nie zostawia tu śladu — ani jako spóźniona, ani jako brak.',
  ];

  // Zero w liczniku „przed czasem" jest zmierzonym zerem TEGO LICZNIKA, a nie dowodem, że nikt nie
  // przyjechał wcześniej. Różnica jest istotna: status nadaje aplikacja kierowcy w chwili dostawy.
  if (m.deliveries > 0 && m.early === 0) {
    lista.push(
      'W tym oknie źródło nie zwróciło ani jednej dostawy przed czasem. To nie dowodzi, że żadna nie ' +
        'przyjechała wcześniej — dowodzi tylko, że żadna nie ma takiego zapisanego statusu.',
    );
  }
  return lista;
}

// ── TRZY WYNIKI ────────────────────────────────────────────────────────────────────────────────

export type Wynik = 'na-czas' | 'przed-czasem' | 'spoznione' | 'nieprzypisane';

export const BARWA_WYNIKU: Record<Wynik, string> = {
  'na-czas': 'var(--sukces)',
  // Świadomie NIE zielony i NIE pomarańczowy: „przed czasem" nie jest ani trafieniem, ani
  // spóźnieniem. Barwa z palety serii mówi „inna kategoria", a nie „lepiej/gorzej".
  'przed-czasem': 'var(--seria-5)',
  spoznione: 'var(--uwaga)',
  nieprzypisane: 'var(--neutralny)',
};

export const ETYKIETA_WYNIKU: Record<Wynik, string> = {
  'na-czas': 'na czas',
  'przed-czasem': 'przed czasem',
  spoznione: 'spóźnione',
  nieprzypisane: 'bez klasyfikacji',
};

export const OPIS_WYNIKU: Record<Wynik, string> = {
  'na-czas': 'trafienie w godzinę zapisaną na rekordzie',
  'przed-czasem': 'przyjazd przed planowaną godziną — osobny wynik, nie trafienie',
  spoznione: 'przyjazd po planowanej godzinie',
  nieprzypisane: 'rekord w oknie, którego źródło nie przypisało do żadnego z trzech wyników',
};

export interface TrzyWyniki {
  dostawy: number;
  naCzas: number;
  przedCzasem: number;
  spoznione: number;
  /** Rekordy w oknie, których suma trzech liczników nie obejmuje. Zwykle zero. */
  nieprzypisane: number;
  /** true, gdy trzy liczniki nie sumują się do liczby dostaw — to jest sygnał, nie ozdoba. */
  rozjazd: boolean;
}

export function trojpodzial(m: DeliveryMetrics): TrzyWyniki {
  const suma = m.on_time + m.early + m.late;
  return {
    dostawy: m.deliveries,
    naCzas: m.on_time,
    przedCzasem: m.early,
    spoznione: m.late,
    nieprzypisane: Math.max(m.deliveries - suma, 0),
    rozjazd: suma !== m.deliveries,
  };
}

/** Udział w procentach albo null, gdy mianownika nie ma. Nigdy 0% z pustego mianownika. */
export function udzial(licznik: number | null, mianownik: number | null): number | null {
  if (licznik === null || mianownik === null || mianownik <= 0) return null;
  return Math.round((licznik / mianownik) * 1000) / 10;
}

export function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v.toLocaleString('pl-PL', { maximumFractionDigits: 1 })}%`;
}

/** Zmiana w punktach procentowych. `null` znaczy „nie da się policzyć", a nie „bez zmian". */
export function formatPunkty(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const znak = v > 0 ? '+' : '';
  return `${znak}${v.toLocaleString('pl-PL', { maximumFractionDigits: 1 })} p.p.`;
}

export function formatMinuty(v: number | null | undefined): string | null {
  if (v === null || v === undefined || !Number.isFinite(v)) return null;
  const znak = v > 0 ? '+' : '';
  return `${znak}${v.toLocaleString('pl-PL', { maximumFractionDigits: 1 })} min`;
}

// ── STATUSY POJEDYNCZEGO REKORDU ───────────────────────────────────────────────────────────────

const GRUPA_STATUSU: Record<string, Wynik> = {
  on_time: 'na-czas',
  early: 'przed-czasem',
  late_5: 'spoznione',
  late_15: 'spoznione',
  late_over_15: 'spoznione',
};

export const ETYKIETA_STATUSU: Record<string, string> = {
  on_time: 'na czas',
  early: 'przed czasem',
  late_5: 'do 5 min',
  late_15: 'do 15 min',
  late_over_15: 'ponad 15 min',
};

/** Status spoza znanego zestawu NIE jest dopasowywany na siłę — zwracamy null i pokazujemy go dosłownie. */
export function grupaStatusu(status: string): Wynik | null {
  return GRUPA_STATUSU[status] ?? null;
}

export function ZnacznikWyniku({ status }: { status: string }) {
  const grupa = grupaStatusu(status);
  const barwa = grupa ? BARWA_WYNIKU[grupa] : 'var(--neutralny)';
  return (
    <Box
      component="span"
      title={
        grupa
          ? OPIS_WYNIKU[grupa]
          : 'Status spoza zestawu znanego Control Center — pokazany dosłownie, bez dopasowania do żadnego z trzech wyników.'
      }
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.25,
        borderRadius: 'var(--r-pigulka)',
        border: `1px solid ${barwa}`,
        color: barwa,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {ETYKIETA_STATUSU[status] ?? status}
    </Box>
  );
}

// ── KOMPLETNOŚĆ LISTY ──────────────────────────────────────────────────────────────────────────

export type Kompletnosc = 'KOMPLETNA' | 'UCIETA' | 'NIEUSTALONA';

/**
 * Czy lista rekordów jest kompletna względem zapytania.
 *
 * „Nieustalona" jest pełnoprawną odpowiedzią: starszy backend nie zwraca `total` ani `truncated`
 * i wtedy interfejs NIE MA PRAWA twierdzić, że widzi komplet. Statystyka policzona z listy, o
 * której nie wiadomo, czy jest pełna, wygląda dokładnie tak samo jak statystyka z pełnej.
 */
export function ocenKompletnosc(w: {
  count: number;
  total?: number;
  limit?: number;
  truncated?: boolean;
}): Kompletnosc {
  if (w.truncated === true) return 'UCIETA';
  if (w.truncated === false) return 'KOMPLETNA';
  if (typeof w.total === 'number') return w.count >= w.total ? 'KOMPLETNA' : 'UCIETA';
  if (typeof w.limit === 'number' && w.count < w.limit) return 'KOMPLETNA';
  return 'NIEUSTALONA';
}

// ── ELEMENTY WSPÓLNE EKRANÓW ───────────────────────────────────────────────────────────────────

/**
 * Klasyfikacja źródła NA EKRANIE, nie tylko w szufladzie dowodu.
 *
 * Dowód trzeba otworzyć; nagłówek widzi każdy, także ten, kto ogląda zrzut ekranu na rzutniku.
 * Skoro zbiór nie jest pełną historią operacyjną, to zdanie musi jechać razem z liczbami.
 */
export function PaskKlasyfikacjiZrodla() {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 3, borderStyle: 'dashed', background: 'var(--powierzchnia-2)' }}
    >
      <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
        Klasyfikacja źródła
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--tekst-2)', maxWidth: '86ch' }}>
        {describeDataOrigin(config.dataOrigin)} · {OPIS_KLASYFIKACJI[KLASYFIKACJA_DRIVE]}. {ZDANIE_O_ZRODLE}
      </Typography>
    </Paper>
  );
}

/** Karta metryki drugiego planu: liczba, jej podstawa, okno, źródło i własny dowód. */
export function KartaMetryki({
  etykieta,
  wartosc,
  zrodlo,
  okno,
  dowod,
  jakosc,
  powodJakosci,
  children,
  kartaId,
}: {
  etykieta: string;
  /** Sformatowana wartość albo null, gdy jej NIE ZNAMY. Null to „brak danych", nigdy zero. */
  wartosc: string | null;
  zrodlo: string;
  okno: string;
  dowod?: Dowod;
  jakosc?: DataQuality;
  powodJakosci?: string;
  children?: ReactNode;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15) — ta sama konwencja co w
   * `PlytaGlowna`, `MiniMetryka`, `MetricCard` i `Karta`.
   *
   * Komponent jest WSPÓŁDZIELONY: na przeglądzie dostaw renderuje się dwa razy (DRIVE-20
   * „Średnie odchylenie" i DRIVE-21 „Gotowe do łączenia ze sprzedażą"). Identyfikator zaszyty
   * w komponencie trafiłby więc na oba wystąpienia naraz i nie rozstrzygnąłby żadnego —
   * musi przyjść z miejsca użycia.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  return (
    <Paper
      variant="outlined"
      data-cc-card-id={kartaId}
      sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}
    >
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
          {etykieta}
        </Typography>
        {jakosc ? <DataQualityBadge quality={jakosc} reason={powodJakosci} /> : null}
      </Stack>

      <Typography
        className="sygnal-liczba"
        sx={{ fontFamily: 'var(--krój-display)', fontSize: 34, lineHeight: 1.1, wordBreak: 'break-word' }}
      >
        {wartosc !== null ? (
          <AnimatedNumberText text={wartosc} />
        ) : (
          <Box component="span" sx={{ fontSize: 17, color: 'var(--tekst-slaby)' }}>
            brak danych
          </Box>
        )}
      </Typography>

      {children}

      <Box sx={{ mt: 'auto', pt: 1.5 }}>
        {dowod ? (
          <Box sx={{ ml: -1, mb: 0.5 }}>
            <PrzyciskDowodu dowod={dowod} />
          </Box>
        ) : null}
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
          Okno: {okno}
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
          Źródło: {zrodlo}
        </Typography>
      </Box>
    </Paper>
  );
}

/**
 * Trójpodział wyniku jako jeden pasek. Nie wykres dla ozdoby: jedna linia pokazuje naraz, ile
 * dostaw trafiło, ile przyjechało za wcześnie i ile się spóźniło — a więc dokładnie to, co ginie,
 * gdy produkt pokazuje wyłącznie „% na czas".
 */
export function PasekTrzechWynikow({
  wyniki,
  podpis,
  kartaId,
}: {
  wyniki: TrzyWyniki;
  podpis?: ReactNode;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15) — ta sama konwencja co w
   * `PlytaGlowna`, `MiniMetryka`, `MetricCard` i `Karta`. Na przeglądzie dostaw to DRIVE-10;
   * podpis w środku niesie własną liczbę i ma własny adres (DRIVE-11), nadawany w miejscu użycia.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  const pozycje: Array<{ klucz: Wynik; liczba: number }> = [
    { klucz: 'na-czas', liczba: wyniki.naCzas },
    { klucz: 'przed-czasem', liczba: wyniki.przedCzasem },
    { klucz: 'spoznione', liczba: wyniki.spoznione },
    ...(wyniki.nieprzypisane > 0 ? [{ klucz: 'nieprzypisane' as const, liczba: wyniki.nieprzypisane }] : []),
  ];
  const suma = pozycje.reduce((acc, p) => acc + p.liczba, 0);
  if (suma <= 0) return null;

  const opisPaska = pozycje
    .map((p) => `${ETYKIETA_WYNIKU[p.klucz]}: ${p.liczba} (${formatPct(udzial(p.liczba, wyniki.dostawy))})`)
    .join(', ');

  return (
    <Paper variant="outlined" data-cc-card-id={kartaId} sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'baseline', flexWrap: 'wrap', mb: 1.5 }}>
        <Typography variant="h6" component="h2">
        Trzy wyniki, nie dwa</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
          udziały liczone jako liczba rekordów ÷ {formatCount(wyniki.dostawy)} rekordów dostaw w oknie
        </Typography>
      </Stack>

      <Box
        role="img"
        aria-label={`Podział dostaw w oknie — ${opisPaska}`}
        sx={{
          display: 'flex',
          height: 26,
          borderRadius: 'var(--r-pigulka)',
          overflow: 'hidden',
          background: 'var(--tlo-glebokie)',
          mb: 2,
        }}
      >
        {pozycje
          .filter((p) => p.liczba > 0)
          .map((p) => (
            <Box
              key={p.klucz}
              className="sygnal-slupek"
              title={`${ETYKIETA_WYNIKU[p.klucz]} — ${OPIS_WYNIKU[p.klucz]}`}
              sx={{ flexGrow: p.liczba, flexBasis: 0, background: BARWA_WYNIKU[p.klucz] }}
            />
          ))}
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
        {pozycje.map((p) => (
          <Box key={p.klucz}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box
                aria-hidden
                sx={{ width: 10, height: 10, borderRadius: 'var(--r-pigulka)', background: BARWA_WYNIKU[p.klucz], flex: 'none' }}
              />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {ETYKIETA_WYNIKU[p.klucz]}
              </Typography>
            </Stack>
            <Typography
              className="sygnal-liczba"
              sx={{ fontFamily: 'var(--krój-display)', fontSize: 26, lineHeight: 1.15, mt: 0.5 }}
            >
              <AnimatedNumber value={p.liczba} format={formatCount} />
              <Box component="span" sx={{ fontSize: 14, color: 'var(--tekst-slaby)', ml: 1 }}>
                <AnimatedNumberText text={formatPct(udzial(p.liczba, wyniki.dostawy))} />
              </Box>
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
              {OPIS_WYNIKU[p.klucz]}
            </Typography>
          </Box>
        ))}
      </Box>

      {podpis ? <Box sx={{ mt: 2 }}>{podpis}</Box> : null}
    </Paper>
  );
}
